import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Play,
  Plus,
  ShieldAlert,
  AlertTriangle,
  Shield,
  ShieldCheck,
  Radar,
  Clock,
  ArrowUpRight,
  Sparkles,
  History,
  Eye,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { fetchRunsRemote, fetchUsageRemote, type RunSnapshot, type UsageInfo } from "@/lib/agentcore";
import { relativeTime, relativeFuture, isStale } from "@/lib/time";
import { UsagePanel } from "./UsagePanel";
import { BriefSearch } from "./BriefSearch";
import type { NovaSynthProfile } from "@/lib/profile";
import type { ThreatLevel } from "@/lib/swarmTypes";

/**
 * Watchlist / Dashboard - the post-login landing screen.
 *
 * Shows each tracked competitor (from profile.competitors) as a card with the
 * latest run's score + level, a trend sparkline (inline SVG from score history),
 * freshness ("Analyzed 2h ago" / "stale"), and a "Run again" action. Degrades
 * gracefully when the runs API returns {ok:false} (table not configured / local
 * dev): chips render in a "not analyzed yet" state - it never crashes.
 */

interface DashboardProps {
  idToken: string;
  profile: NovaSynthProfile | null;
  /** Launch an analysis for a competitor (navigates to the analyze route). */
  onAnalyze: (competitor: string) => void;
}

const LEVEL: Record<ThreatLevel, { label: string; Icon: LucideIcon; hex: string; text: string; chip: string }> = {
  CRITICAL: { label: "Critical", Icon: ShieldAlert, hex: "#ef4444", text: "text-red-400", chip: "border-red-700/40 bg-red-500/10 text-red-300" },
  HIGH: { label: "High", Icon: AlertTriangle, hex: "#f59e0b", text: "text-amber-400", chip: "border-amber-600/40 bg-amber-500/10 text-amber-300" },
  MEDIUM: { label: "Medium", Icon: Shield, hex: "#22d3ee", text: "text-cyan-300", chip: "border-cyan-600/40 bg-cyan-500/10 text-cyan-300" },
  LOW: { label: "Low", Icon: ShieldCheck, hex: "#94a3b8", text: "text-slate-300", chip: "border-slate-600/40 bg-white/5 text-slate-300" },
};

export function Dashboard({ idToken, profile, onAnalyze }: DashboardProps) {
  const navigate = useNavigate();
  const competitors = useMemo(() => profile?.competitors ?? [], [profile]);

  // runs keyed by competitor (lowercased), newest-first; runsOk=false means the
  // store is unavailable (local dev / not configured) - show the empty-ish state.
  const [runsByCompetitor, setRunsByCompetitor] = useState<Record<string, RunSnapshot[]>>({});
  // Flat, newest-first list of ALL runs (tracked OR ad-hoc) for "Recent analyses".
  const [allRuns, setAllRuns] = useState<RunSnapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [runsOk, setRunsOk] = useState<boolean>(true);
  // Daily allowance, surfaced as a subtle header chip. Null when the feature is
  // unconfigured (local dev) -> the chip simply does not render.
  const [usage, setUsage] = useState<UsageInfo | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      const res = await fetchRunsRemote(idToken, { limit: 100 });
      if (cancelled) return;
      if (!res.ok) {
        setRunsOk(false);
        setRunsByCompetitor({});
        setAllRuns([]);
        setLoading(false);
        return;
      }
      setRunsOk(true);
      const all = [...(res.runs ?? [])].sort((a, b) => Date.parse(b.runAt) - Date.parse(a.runAt));
      setAllRuns(all);
      const grouped: Record<string, RunSnapshot[]> = {};
      for (const r of all) {
        const key = r.competitor.trim().toLowerCase();
        (grouped[key] ??= []).push(r);
      }
      // grouped buckets inherit `all`'s newest-first order.
      setRunsByCompetitor(grouped);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [idToken]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const u = await fetchUsageRemote(idToken);
      if (!cancelled) setUsage(u);
    })();
    return () => { cancelled = true; };
  }, [idToken]);

  const hasAnyRuns = Object.keys(runsByCompetitor).length > 0;

  // Tracked-set (lowercased) so Recent analyses can flag tracked vs ad-hoc.
  const trackedSet = useMemo(
    () => new Set(competitors.map((c) => c.trim().toLowerCase())),
    [competitors],
  );

  // "Recent analyses" = the latest run for each distinct company, newest-first,
  // including ad-hoc companies the user analyzed that are NOT on the watchlist.
  const recent = useMemo(() => {
    const seen = new Set<string>();
    const out: RunSnapshot[] = [];
    for (const r of allRuns) {
      const key = r.competitor.trim().toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(r);
    }
    return out.slice(0, 8);
  }, [allRuns]);

  return (
    <div className="flex flex-col gap-7 w-full">
      {/* Heading */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Radar className="w-3.5 h-3.5 text-[#22d3ee]" />
            <span className="text-[10px] font-semibold text-[#22d3ee] uppercase tracking-[0.2em]">Watchlist</span>
          </div>
          <h1 className="font-mono text-2xl sm:text-3xl font-bold tracking-tight text-white">
            {profile?.companyName ? `${profile.companyName}'s competitive landscape` : "Your competitive landscape"}
          </h1>
          <p className="mt-1.5 text-sm text-slate-500 max-w-2xl leading-relaxed">
            Every competitor you track, with its latest threat read at a glance. Re-run any one to refresh the brief.
          </p>
          {/* Subtle allowance chip. Daily-focused, but when the MONTH is the binding
              constraint (its remaining is the smaller wall) the title surfaces that.
              Renders only when an allowance is known (feature configured) and active -
              otherwise nothing, so unconfigured backends / local dev show no chip. */}
          {usage && usage.status === "active" && (usage.daily.limit > 0 || usage.monthly.limit > 0) && (
            <span
              className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-[#1a2640] bg-white/[0.03] px-2.5 py-1 text-[11px] font-medium text-slate-400"
              title={
                usage.monthly.limit > 0 && usage.monthly.remaining <= 0
                  ? `More analyses open up ${relativeFuture(usage.monthly.resetsAt) || "next month"}.`
                  : usage.daily.limit > 0 && usage.daily.remaining <= 0
                    ? `More analyses open up ${relativeFuture(usage.daily.resetsAt) || "soon"}.`
                    : usage.monthly.limit > 0
                      ? `${usage.daily.remaining} of ${usage.daily.limit} left today; ${usage.monthly.remaining} of ${usage.monthly.limit} left this month.`
                      : `${usage.daily.remaining} of ${usage.daily.limit} analyses left today.`
              }
            >
              <Sparkles className="w-3 h-3 text-[#22d3ee]" />
              {usage.daily.limit > 0
                ? `Today: ${usage.daily.used} of ${usage.daily.limit} analyses used`
                : `This month: ${usage.monthly.used} of ${usage.monthly.limit} analyses used`}
            </span>
          )}
        </div>
        <button
          onClick={() => navigate("/app/analyze")}
          className="shrink-0 inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-brand-600 to-brand-500 px-5 py-3 text-sm font-semibold text-white shadow-glow-brand-sm transition-all duration-200 hover:from-brand-500 hover:to-brand-400 hover:shadow-glow-brand cursor-pointer min-h-[44px]"
        >
          <Play className="w-4 h-4 fill-current" />
          New analysis
        </button>
      </div>

      {/* Your usage - clear daily + monthly awareness (degrades silently). */}
      <UsagePanel usage={usage} />

      {/* Semantic search across past briefs (degrades silently when unconfigured). */}
      <BriefSearch idToken={idToken} onOpen={(name) => navigate(`/app/competitor/${encodeURIComponent(name)}`)} />

      {/* No tracked competitors at all */}
      {competitors.length === 0 ? (
        <EmptyWatchlist onAnalyze={() => navigate("/app/analyze")} />
      ) : (
        <>
          {/* Soft notice when the history store isn't available yet */}
          {!loading && !runsOk && (
            <div className="rounded-xl border border-[#1a2640] bg-white/[0.02] px-4 py-3 text-xs text-slate-500 flex items-center gap-2">
              <Clock className="w-3.5 h-3.5 text-slate-600 shrink-0" />
              Run history will appear here once your first briefs complete.
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {competitors.map((c, i) => {
              const runs = runsByCompetitor[c.trim().toLowerCase()] ?? [];
              return (
                <CompetitorCard
                  key={c}
                  competitor={c}
                  runs={runs}
                  loading={loading && runsOk}
                  index={i}
                  onOpen={() => navigate(`/app/competitor/${encodeURIComponent(c)}`)}
                  onRun={() => onAnalyze(c)}
                />
              );
            })}
          </div>

          {/* When the store works but nothing has run yet */}
          {!loading && runsOk && !hasAnyRuns && (
            <p className="text-center text-xs text-slate-600">
              No briefs yet - pick a competitor above and run your first analysis.
            </p>
          )}
        </>
      )}

      {/* Recent analyses - EVERY run (tracked or ad-hoc), newest-first. Lets
          one-off searches that aren't on the watchlist still be found + reopened. */}
      {runsOk && recent.length > 0 && (
        <RecentAnalyses
          runs={recent}
          trackedSet={trackedSet}
          onOpen={(name) => navigate(`/app/competitor/${encodeURIComponent(name)}`)}
        />
      )}
    </div>
  );
}

// ── Recent analyses (all run history, tracked + ad-hoc) ──────────────────────

function RecentAnalyses({
  runs,
  trackedSet,
  onOpen,
}: {
  runs: RunSnapshot[];
  trackedSet: Set<string>;
  onOpen: (competitor: string) => void;
}) {
  return (
    <section className="ns-glass rounded-2xl p-5 sm:p-6 shadow-card">
      <div className="flex items-center gap-2 mb-1">
        <History className="w-3.5 h-3.5 text-[#22d3ee]" aria-hidden="true" />
        <span className="text-[10px] font-semibold text-[#22d3ee] uppercase tracking-[0.2em]">Recent analyses</span>
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Everything you have analyzed recently - including one-off searches that aren't on your watchlist.
      </p>
      <ul className="flex flex-col gap-2.5">
        {runs.map((r) => {
          const theme = LEVEL[r.level];
          const tracked = trackedSet.has(r.competitor.trim().toLowerCase());
          return (
            <li key={`${r.competitor}-${r.runId}`}>
              <button
                type="button"
                onClick={() => onOpen(r.competitor)}
                className="group flex w-full items-center justify-between gap-3 rounded-xl border border-white/5 bg-white/[0.02] px-3.5 py-3 text-left transition-colors hover:border-[#22d3ee]/30 hover:bg-white/[0.04] cursor-pointer"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className={`flex h-9 w-9 items-center justify-center rounded-lg border shrink-0 ${theme.chip}`}>
                    <span className={`font-mono text-sm font-bold ${theme.text}`}>{r.score}</span>
                  </span>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-mono text-sm font-bold text-white truncate group-hover:text-[#22d3ee] transition-colors">
                        {r.competitor}
                      </span>
                      {tracked ? (
                        <span className="inline-flex items-center gap-1 rounded-full border border-[#22d3ee]/30 bg-[#22d3ee]/5 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-[#22d3ee] shrink-0">
                          <Eye className="w-2.5 h-2.5" aria-hidden="true" />
                          Tracked
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full border border-[#1a2640] bg-white/[0.03] px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-slate-500 shrink-0">
                          One-off
                        </span>
                      )}
                    </div>
                    <p className="mt-0.5 text-[11px] text-slate-500">
                      <span className={`font-medium ${theme.text}`}>{theme.label}</span>
                      <span className="mx-1.5 text-slate-700">&middot;</span>
                      Analyzed {relativeTime(r.runAt)}
                    </p>
                  </div>
                </div>
                <ArrowUpRight className="w-4 h-4 text-slate-600 group-hover:text-[#22d3ee] transition-colors shrink-0" />
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// ── Competitor card ──────────────────────────────────────────────────────────

function CompetitorCard({
  competitor,
  runs,
  loading,
  index,
  onOpen,
  onRun,
}: {
  competitor: string;
  runs: RunSnapshot[];
  loading: boolean;
  index: number;
  onOpen: () => void;
  onRun: () => void;
}) {
  const latest = runs[0] ?? null;
  const theme = latest ? LEVEL[latest.level] : null;
  const scores = [...runs].reverse().map((r) => r.score); // oldest → newest
  const stale = latest ? isStale(latest.runAt) : false;

  return (
    <div
      className="ns-reveal group relative flex flex-col rounded-2xl border border-[#1a2640] bg-[#0d1424]/70 p-5 transition-all duration-200 hover:-translate-y-0.5 hover:border-white/15"
      style={{ ["--ns-delay" as string]: `${80 + index * 60}ms` }}
    >
      {/* Header: name + open */}
      <button
        type="button"
        onClick={onOpen}
        className="flex items-start justify-between gap-2 text-left cursor-pointer"
      >
        <div className="min-w-0">
          <p className="font-mono text-base font-bold text-white truncate group-hover:text-[#22d3ee] transition-colors">
            {competitor}
          </p>
          <p className="mt-0.5 text-[11px] text-slate-600">
            {loading ? "Loading…" : latest ? `Analyzed ${relativeTime(latest.runAt)}` : "Not analyzed yet"}
          </p>
        </div>
        <ArrowUpRight className="w-4 h-4 text-slate-600 group-hover:text-[#22d3ee] transition-colors shrink-0" />
      </button>

      {/* Body: score + level + sparkline, or empty state */}
      <div className="mt-4 flex-1">
        {loading ? (
          <div className="space-y-2">
            <div className="h-7 w-20 rounded bg-white/5 animate-pulse" />
            <div className="h-8 w-full rounded bg-white/5 animate-pulse" />
          </div>
        ) : latest && theme ? (
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="flex items-baseline gap-1.5">
                <span className={`font-mono text-3xl font-bold leading-none ${theme.text}`}>{latest.score}</span>
                <span className="text-[11px] font-mono text-slate-600">/ 10</span>
              </div>
              <span className={`mt-2 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${theme.chip}`}>
                <theme.Icon className="w-3 h-3" />
                {theme.label}
              </span>
            </div>
            <Sparkline scores={scores} hex={theme.hex} />
          </div>
        ) : (
          <div className="flex flex-col items-start gap-1.5 py-2">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-[#1a2640] bg-white/[0.03] px-2.5 py-1 text-[11px] text-slate-500">
              <span className="w-1.5 h-1.5 rounded-full bg-slate-600" />
              Not analyzed yet
            </span>
            <p className="text-xs text-slate-600">Run a first brief to start tracking the threat trend.</p>
          </div>
        )}
      </div>

      {/* Footer: freshness badge + run */}
      <div className="mt-4 flex items-center justify-between gap-2 border-t border-white/5 pt-3">
        {stale ? (
          <span className="inline-flex items-center gap-1 rounded-full border border-amber-600/30 bg-amber-500/5 px-2 py-0.5 text-[10px] font-medium text-amber-400">
            <Clock className="w-3 h-3" /> Stale
          </span>
        ) : latest ? (
          <span className="inline-flex items-center gap-1 rounded-full border border-emerald-700/25 bg-emerald-500/5 px-2 py-0.5 text-[10px] font-medium text-emerald-300">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Fresh
          </span>
        ) : (
          <span className="text-[10px] text-slate-700">-</span>
        )}
        <button
          type="button"
          onClick={onRun}
          className="inline-flex items-center gap-1.5 rounded-lg border border-[#1a2640] bg-white/[0.03] px-3 py-1.5 text-xs font-semibold text-slate-300 transition-colors hover:border-[#22d3ee]/40 hover:text-[#22d3ee] cursor-pointer min-h-[36px]"
        >
          <Play className="w-3 h-3 fill-current" />
          {latest ? "Run again" : "Run"}
        </button>
      </div>
    </div>
  );
}

// ── Trend sparkline (inline SVG from score history) ──────────────────────────

export function Sparkline({ scores, hex, width = 96, height = 36 }: { scores: number[]; hex: string; width?: number; height?: number }) {
  if (scores.length === 0) {
    return <div style={{ width, height }} className="rounded bg-white/[0.02] border border-white/5" aria-hidden="true" />;
  }
  if (scores.length === 1) {
    // A single data point - render a dot at its level.
    const y = height - (scores[0] / 10) * (height - 6) - 3;
    return (
      <svg width={width} height={height} aria-hidden="true">
        <circle cx={width - 5} cy={y} r={3} fill={hex} />
      </svg>
    );
  }
  const pad = 3;
  const max = 10;
  const stepX = (width - pad * 2) / (scores.length - 1);
  const pts = scores.map((s, i) => {
    const x = pad + i * stepX;
    const y = height - pad - (Math.max(0, Math.min(max, s)) / max) * (height - pad * 2);
    return [x, y] as const;
  });
  const d = pts.map(([x, y], i) => `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`).join(" ");
  const last = pts[pts.length - 1];
  const areaD = `${d} L ${last[0].toFixed(1)} ${height - pad} L ${pts[0][0].toFixed(1)} ${height - pad} Z`;
  const gid = `spark-${hex.replace("#", "")}`;

  return (
    <svg width={width} height={height} aria-label="Threat score trend" role="img">
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={hex} stopOpacity="0.28" />
          <stop offset="100%" stopColor={hex} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaD} fill={`url(#${gid})`} />
      <path d={d} fill="none" stroke={hex} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={last[0]} cy={last[1]} r={2.6} fill={hex} />
    </svg>
  );
}

// ── Empty watchlist (no tracked competitors) ─────────────────────────────────

function EmptyWatchlist({ onAnalyze }: { onAnalyze: () => void }) {
  return (
    <div className="ns-glass rounded-2xl p-10 text-center flex flex-col items-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-700/25 border border-brand-600/30 text-brand-300">
        <Plus className="w-6 h-6" />
      </div>
      <h2 className="mt-4 font-mono text-lg font-bold text-white">No competitors tracked yet</h2>
      <p className="mt-2 max-w-md text-sm text-slate-500 leading-relaxed">
        Add competitors in Settings to build your watchlist - or run a one-off analysis on any company right now.
      </p>
      <button
        onClick={onAnalyze}
        className="mt-5 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-brand-600 to-brand-500 px-5 py-3 text-sm font-semibold text-white shadow-glow-brand-sm transition-all duration-200 hover:from-brand-500 hover:to-brand-400 cursor-pointer min-h-[44px]"
      >
        <Play className="w-4 h-4 fill-current" />
        Run an analysis
      </button>
    </div>
  );
}
